/**
 * Access Level Constants — Single source of truth for content library access control.
 *
 * Tier model:
 *   public  — no login needed
 *   paid    — has ever spent money with this coach (permanent flag)
 *   member  — active subscription (Phase 2)
 *
 * Legacy mapping (handled transparently at read time):
 *   everyone → public
 *   members  → paid
 *   paid     → member
 */

// --- Enums ---

export const ACCESS_LEVELS = {
  PUBLIC: 'public',
  PAID: 'paid',
  MEMBER: 'member',
  STUDENTS: 'students',
  ADMIN: 'admin',
};

export const USER_TIERS = {
  VISITOR: 'visitor',
  AUTHENTICATED: 'authenticated',
  PAID: 'paid',
  MEMBER: 'member',
  OWNER: 'owner',
};

// --- Legacy mapping ---

const LEGACY_ACCESS_MAP = {
  everyone: ACCESS_LEVELS.PUBLIC,
  members: ACCESS_LEVELS.PAID,
  paid: ACCESS_LEVELS.MEMBER,
};

/**
 * Translate any stored access level value (old or new) to the current enum.
 * Returns PUBLIC as the safe default for unknown values.
 */
export function normalizeAccessLevel(raw) {
  if (!raw) return ACCESS_LEVELS.PUBLIC;
  const lower = String(raw).toLowerCase().trim();

  // Already a current value
  if (Object.values(ACCESS_LEVELS).includes(lower)) return lower;

  // Legacy value
  if (LEGACY_ACCESS_MAP[lower]) return LEGACY_ACCESS_MAP[lower];

  return ACCESS_LEVELS.PUBLIC;
}

// --- Display config ---

export const ACCESS_LEVEL_CONFIG = {
  [ACCESS_LEVELS.PUBLIC]: {
    label: 'Free',
    description: 'Visible to everyone, no login required',
    color: '#22c55e',
    cssClass: 'access-public',
    icon: 'Globe',
  },
  [ACCESS_LEVELS.PAID]: {
    label: 'Students',
    description: 'Visible to anyone who has purchased from you',
    color: '#f59e0b',
    cssClass: 'access-paid',
    icon: 'ShoppingBag',
  },
  [ACCESS_LEVELS.MEMBER]: {
    label: 'Members',
    description: 'Visible to active subscribers only',
    color: '#8b5cf6',
    cssClass: 'access-member',
    icon: 'Crown',
  },
  [ACCESS_LEVELS.STUDENTS]: {
    label: 'Specific Students',
    description: 'Hand-pick which students can access this content',
    color: '#3b82f6',
    cssClass: 'access-students',
    icon: 'UserCheck',
  },
  [ACCESS_LEVELS.ADMIN]: {
    label: 'Admin Only',
    description: 'Only visible to you on the admin dashboard',
    color: '#ef4444',
    cssClass: 'access-admin',
    icon: 'ShieldAlert',
  },
};

// --- Membership-aware options ---

/**
 * Return the access-level options relevant to a coach's configuration.
 *
 * hasMembership = true  → PUBLIC, MEMBER, STUDENTS  (exclude PAID)
 * hasMembership = false → PUBLIC, PAID (relabeled "Past Students"), STUDENTS  (exclude MEMBER)
 */
export function getAccessLevelOptions(hasMembership) {
  if (hasMembership) {
    return {
      [ACCESS_LEVELS.PUBLIC]: ACCESS_LEVEL_CONFIG[ACCESS_LEVELS.PUBLIC],
      [ACCESS_LEVELS.MEMBER]: ACCESS_LEVEL_CONFIG[ACCESS_LEVELS.MEMBER],
      [ACCESS_LEVELS.STUDENTS]: ACCESS_LEVEL_CONFIG[ACCESS_LEVELS.STUDENTS],
      [ACCESS_LEVELS.ADMIN]: ACCESS_LEVEL_CONFIG[ACCESS_LEVELS.ADMIN],
    };
  }

  return {
    [ACCESS_LEVELS.PUBLIC]: ACCESS_LEVEL_CONFIG[ACCESS_LEVELS.PUBLIC],
    [ACCESS_LEVELS.PAID]: {
      ...ACCESS_LEVEL_CONFIG[ACCESS_LEVELS.PAID],
      label: 'Past Students',
      description: 'Visible to anyone who has booked a lesson with you',
    },
    [ACCESS_LEVELS.STUDENTS]: ACCESS_LEVEL_CONFIG[ACCESS_LEVELS.STUDENTS],
    [ACCESS_LEVELS.ADMIN]: ACCESS_LEVEL_CONFIG[ACCESS_LEVELS.ADMIN],
  };
}

// --- Permission check ---

/** Ordered tiers from lowest to highest access. */
const TIER_ORDER = [
  USER_TIERS.VISITOR,
  USER_TIERS.AUTHENTICATED,
  USER_TIERS.PAID,
  USER_TIERS.MEMBER,
  USER_TIERS.OWNER,
];

/** Map access level required → minimum user tier needed. */
const ACCESS_TIER_REQUIREMENT = {
  [ACCESS_LEVELS.PUBLIC]: USER_TIERS.VISITOR,
  [ACCESS_LEVELS.PAID]: USER_TIERS.PAID,
  [ACCESS_LEVELS.MEMBER]: USER_TIERS.MEMBER,
};

/**
 * Check whether a user tier satisfies an item's access level.
 * Owner always has access.
 */
export function canAccessContent(userTier, itemAccessLevel) {
  if (userTier === USER_TIERS.OWNER) return true;

  const normalized = normalizeAccessLevel(itemAccessLevel);

  // Admin-only items are never accessible to non-owners
  if (normalized === ACCESS_LEVELS.ADMIN) return false;

  // Student-gated items require an item-level allowedStudentIds check (handled by canAccessItem)
  if (normalized === ACCESS_LEVELS.STUDENTS) return false;

  const requiredTier = ACCESS_TIER_REQUIREMENT[normalized] || USER_TIERS.VISITOR;

  const userRank = TIER_ORDER.indexOf(userTier);
  const requiredRank = TIER_ORDER.indexOf(requiredTier);

  if (userRank === -1 || requiredRank === -1) return false;
  return userRank >= requiredRank;
}
